import React, { useEffect, useState } from "react";
import { cqAPI } from "../../lib/Auth";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Button,
  useTheme,
  useColorMode,
  IconButton,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Checkbox
} from "@chakra-ui/react";
import EditableText from "../EditableText/EditableText";
import { useNavigate } from 'react-router-dom';
import { AddIcon, ChevronDownIcon, ChevronUpIcon, ChevronRightIcon } from "@chakra-ui/icons";

function AllPartners() {
  const [partners, setPartners] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const navigate = useNavigate();

  const {
    isOpen: isNewPartnerOpen,
    onOpen: onNewPartnerOpen,
    onClose: onNewPartnerClose
  } = useDisclosure();

  const theme = useTheme();
  const { colorMode } = useColorMode();
  const hoverBgColor =
    colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];
  const toast = useToast();

  const fetchAllPartners = async () => {
    const response = await cqAPI.get(`/settings/partners`);
    if (response?.type === "ok" && response.data?.partners) {
      setPartners(response.data.partners);
    }
  };

  useEffect(() => {
    fetchAllPartners();
  }, []);

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const updatePartnerProfile = async (partner_id, field, value) => {
    const response = await cqAPI.put(`/settings/partners`, {
      partner_id,
      name: value,
    });
    if (response.type === "ok") {
      setPartners(
        partners.map((partner) => {
          if (partner._id === partner_id) {
            partner.name = value;
          }
          return partner;
        })
      );
      return true;
    } else {
      if (response.msg) {
        toast({
          title: "Error updating partner",
          description: response.msg,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
      return false;
    }
  };

  // Updated to accept 'overageFeeShare' as an argument
  const createPartner = async (name, invoiced, email, overageFeeShare) => {
    const response = await cqAPI.post("/settings/partners", {
      name,
      invoiced,
      email,
      overage_fee_share: overageFeeShare, // <-- pass to API
    });
    if (response.type === "ok") {
      await fetchAllPartners();
      return true; // indicate success
    } else {
      if (response.msg) {
        toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
      return false; // indicate failure
    }
  };

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <NewPartnerModal
        isOpen={isNewPartnerOpen}
        onClose={onNewPartnerClose}
        onSave={createPartner} // Pass the function with the new signature
      />

      <Flex justifyContent="flex-end" mb={4}>
        <Button leftIcon={<AddIcon />} onClick={onNewPartnerOpen}>
          Add Partner
        </Button>
      </Flex>

      <VStack spacing={4} align="stretch">
        {partners.map((partner) => (
          <Box key={partner._id}>
            <Flex
              p={2}
              _hover={{ bg: hoverBgColor, cursor: "pointer" }}
              justifyContent="space-between"
              id={`partner-${partner._id}`}
            >
              <HStack spacing={8} flex="1">
                <Box minWidth="40px">
                  {partner.customers && partner.customers.length > 0 && (
                    <IconButton
                      icon={
                        expandedRows[partner._id] ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      onClick={() => toggleExpand(partner._id)}
                      aria-label="Expand"
                    />
                  )}
                </Box>
                <Flex flexBasis="90%" alignItems={"center"}>
                  <EditableText
                    initialValue={partner.name}
                    placeholder="Partner Name"
                    pattern={/[a-zA-Z0-9]+/}
                    invalid="Cannot be empty"
                    onSave={(newValue) =>
                      updatePartnerProfile(partner._id, "name", newValue)
                    }
                  />
                  <IconButton
                    id={`partner-profile-${partner._id}`}
                    ml={5}
                    onClick={() => navigate(`/partners/${partner._id}`)}
                    icon={<ChevronRightIcon />}
                    variant="ghost"
                    aria-label={open}/>
                </Flex>
              </HStack>
            </Flex>
            {expandedRows[partner._id] && partner.customers && (
              <VStack align="stretch" pl={12} mt={2}>
                {partner.customers.map((cust) => (
                  <Text textAlign="left" key={cust._id} fontSize="sm">
                    {cust.name}
                  </Text>
                ))}
              </VStack>
            )}
          </Box>
        ))}
      </VStack>
    </Box>
  );
}

function NewPartnerModal({ isOpen, onClose, onSave }) {
  const [name, setName] = useState("");
  const [invoiced, setInvoiced] = useState(false);
  const [email, setEmail] = useState("");
  const [overageFeeShare, setOverageFeeShare] = useState("");
  const toast = useToast();

  const handleSave = async () => {
    // Validate name
    if (!name.trim()) {
      toast({
        title: "Invalid Input",
        description: "Partner name cannot be empty.",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      return;
    }

    // Validate email (basic validation check)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim() || !emailRegex.test(email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      return;
    }

    // Validate overageFeeShare (must be a number between 0 and 100)
    const numericShare = parseFloat(overageFeeShare);
    if (
      isNaN(numericShare) ||
      numericShare < 0 ||
      numericShare > 100
    ) {
      toast({
        title: "Invalid Overages Split %",
        description: "Overage split must be a number between 0 and 100.",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      return;
    }

    // Pass all values along to onSave
    const success = await onSave(name, invoiced, email, numericShare);
    if (success) {
      // Reset local state and close modal
      setName("");
      setInvoiced(false);
      setEmail("");
      setOverageFeeShare("");
      onClose();
    }
  };

  const handleClose = () => {
    // Reset state on cancel/close
    setName("");
    setInvoiced(false);
    setEmail("");
    setOverageFeeShare("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add a Partner</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl id="partner-name" mb={4} isRequired>
            <FormLabel>Partner Name</FormLabel>
            <Input
              type="text"
              placeholder="Enter partner name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </FormControl>

          <FormControl id="partner-email" mb={4} isRequired>
            <FormLabel>Partner Email</FormLabel>
            <Input
              type="email"
              placeholder="Enter partner email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </FormControl>

          <FormControl id="partner-invoiced" mb={4}>
            <Checkbox
              isChecked={invoiced}
              onChange={(e) => setInvoiced(e.target.checked)}
            >
              Partner does the invoicing
            </Checkbox>
          </FormControl>

          <FormControl id="partner-overage-split" mb={4} isRequired>
            <FormLabel>Overage Split % for partner</FormLabel>
            <Input
              type="number"
              placeholder="Enter overage split % (0 to 100)"
              value={overageFeeShare}
              onChange={(e) => setOverageFeeShare(e.target.value)}
            />
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={handleSave}>
            Save
          </Button>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default AllPartners;
